import {css, StyleSheet} from 'aphrodite';
import PropTypes from 'prop-types';
import React, {createRef} from 'react';
import ReactDOM from 'react-dom';

import * as benchlingUtil from 'BenchlingUtil';
import {
  linkComponentDidMount,
  linkComponentDidUpdate,
  linkComponentWillUnmount,
  openLink,
} from 'link-preview/LinkUtil';
import * as Colors from 'ui-library/Colors';
import {ElementMouseEvent} from 'util/Types';

import {HYPERLINK_STATE_SHAPE, HyperlinkState} from '../selection/text/SelectionReader';

export interface PositionedLinkPopoverProps {
  hyperlinkState: HyperlinkState;
  onChange: (e: ElementMouseEvent) => void;
  onRemove: (e: ElementMouseEvent) => void;
  containerWidth: number;
}

/**
 * Show link in a popover when user clicks on link.
 */
class PositionedLinkPopover extends React.Component<PositionedLinkPopoverProps> {
  static propTypes = {
    hyperlinkState: HYPERLINK_STATE_SHAPE.isRequired,
    onChange: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    containerWidth: PropTypes.number.isRequired,
  };

  static WIDTH = 336;
  static MARGIN = 20;
  static VERTICAL_MARGIN = 5;
  static MAX_LENGTH_URL = 50;

  urlMap: {[url: string]: number} = {};

  divRef = createRef<HTMLDivElement>();

  componentDidMount(): void {
    this.urlMap = linkComponentDidMount(ReactDOM.findDOMNode(this) as Element);
  }

  componentDidUpdate(): void {
    this.urlMap = linkComponentDidUpdate(ReactDOM.findDOMNode(this) as Element, this.urlMap);
  }

  componentWillUnmount(): void {
    linkComponentWillUnmount(ReactDOM.findDOMNode(this) as Element);
  }

  _handleClick = (e: ElementMouseEvent): void => {
    if (!benchlingUtil.isSimpleClick(e)) {
      return;
    }
    e.preventDefault();
    openLink(this.props.hyperlinkState.link.url);
  };

  _renderLink(): JSX.Element {
    return (
      <a
        className={css(PositionedLinkPopoverStyles.hyperlink)}
        rel="noopener"
        href={this.props.hyperlinkState.link.url}
        onClick={this._handleClick}
      >
        {this.props.hyperlinkState.link.url}
      </a>
    );
  }
  _calculateContentDivHeight(): number {
    if (this.divRef.current) {
      const style = window.getComputedStyle(this.divRef.current);
      const marginTop = parseFloat(style.marginTop);
      const marginBottom = parseFloat(style.marginBottom);
      return this.divRef.current.offsetHeight + marginTop + marginBottom;
    }
    return 0;
  }

  render(): JSX.Element {
    const maxLeft = this.props.containerWidth - PositionedLinkPopover.WIDTH - PositionedLinkPopover.MARGIN;
    // Drop up if there is not enough space to render the popup between the anchor and the bottom of the viewport
    const contentHeight = this._calculateContentDivHeight();
    const box = this.props.hyperlinkState.box;
    const dropUp = box.distanceFromViewportBottom < contentHeight + PositionedLinkPopover.VERTICAL_MARGIN;
    const top = dropUp ? box.top : box.bottom;
    const left = Math.min(box.left, maxLeft);
    return (
      <div
        className={css(PositionedLinkPopoverStyles.popoverWrapper)}
        style={{
          top,
          left,
        }}
      >
        <div
          ref={this.divRef}
          className={css(
            PositionedLinkPopoverStyles.popoverContent,
            dropUp && PositionedLinkPopoverStyles.dropUpPopover
          )}
        >
          {this._renderLink()}
          <span className={css(PositionedLinkPopoverStyles.buttons)}>
            <button
              className="button button--tertiaryGray button--mini"
              onClick={this.props.onRemove}
              type="button"
            >
              Remove
            </button>
            <button
              className="button button--secondary button--mini u-mini-spacing-left"
              onClick={this.props.onChange}
              type="button"
            >
              Change
            </button>
          </span>
        </div>
      </div>
    );
  }
}

export const PositionedLinkPopoverStyles = StyleSheet.create({
  dropUpPopover: {
    transform: `translateY(calc(-100% - ${PositionedLinkPopover.VERTICAL_MARGIN}px))`,
    boxShadow: '0 -5px 10px rgba(0, 0, 0, 0.2)',
  },
  popoverWrapper: {
    position: 'relative',
    maxWidth: 'fit-content',
  },
  popoverContent: {
    position: 'absolute',
    margin: `${PositionedLinkPopover.VERTICAL_MARGIN}px 0`,
    width: PositionedLinkPopover.WIDTH,

    // ### based on Less class .positionedLinkPopover-content (deleted)
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    // ###

    // ### based on Less class .positionedPopover
    background: 'white',
    padding: '10px 12px',
    border: `1px solid ${Colors.GRAY_LIGHT}`,
    borderRadius: '4px',
    boxShadow: '0 5px 10px rgba(0, 0, 0, 0.2)',
    zIndex: 1000,
    // ###
  },
  hyperlink: {
    // ### based on Less class .u-ellipsis-text
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    // ###

    // based on Less class .positionedLinkPopover-link (deleted)
    padding: '5px 0',
  },
  buttons: {
    // based on Less class .positionedLinkPopover-buttons (deleted)
    padding: '5px 0',
  },
});

export default PositionedLinkPopover;
